import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search, ArrowRight, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin lands here, picks a vendor from the searchable list, clicks
 * "Continue" and gets forwarded to the shared campaign wizard mounted
 * at `/vendor/campaigns/new?adminForVendor=<id>`. The wizard then
 * routes submit through the admin-shim endpoint — so the vendor is
 * still the owner and payer of record, and no wizard logic is forked.
 */
export default function AdminCreateCampaignPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.adminVendors()
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Couldn't load vendors"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = vendors.filter((v) => {
    if (!q.trim()) return true;
    const n = q.trim().toLowerCase();
    return (
      (v.business || "").toLowerCase().includes(n) ||
      (v.name || "").toLowerCase().includes(n) ||
      (v.email || "").toLowerCase().includes(n) ||
      (v.phone || "").toLowerCase().includes(n)
    );
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
          <Building2 className="h-6 w-6 text-[#F5A800]" /> Create campaign on behalf of vendor
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick the target vendor — billing follows their trial/plan just like a self-serve create.
        </p>
      </div>

      <Card className="p-4">
        <Label htmlFor="vs-search">Search by business, name, email or phone</Label>
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="vs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Cafe X, +91…"
            className="pl-9"
            data-testid="admin-create-campaign-search"
            autoFocus
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading vendors…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            {q ? "No vendors match your search." : "No vendors yet."}
          </div>
        ) : (
          <div className="divide-y">
            {filtered.slice(0, 100).map((v) => (
              <button
                type="button"
                key={v.id}
                onClick={() => navigate(`/vendor/campaigns/new?adminForVendor=${v.id}`)}
                className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left"
                data-testid={`admin-create-campaign-vendor-${v.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{v.business || "(unnamed)"}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {v.name || "—"} · {v.email || "—"} · {v.city || "—"} · plan: {v.plan || "—"}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => navigate("/admin")} data-testid="admin-create-campaign-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}
